import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CommonFilter } from "../../Components/Common/CommonFilter";
import { getActiveThreadTypesArr, getThreadTypesArr, getUsersArr, checkIfInqOnlyMode, isSmartSellerMode } from "../../Store/Reducers";
import { MdPhone } from 'react-icons/md';
import { BsWhatsapp } from 'react-icons/bs';
import { timeAgo, getTaskColor, getColorClass, getLastDateColor, FormatAmount } from '../../Utils/Utilities';
import { getTableDateOrWeekday } from "../../Utils/DateTime";
import { getDateRangeOptions, getPastMonthOptions, getPastQuarterOptions, getOtherYearOptions } from '../../Utils/Utilities';
import ThrDetails from './ThrDetails';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, useDroppable, useDraggable } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { updateThreadStageAPI, toggleThreadStarAPI } from './ThreadsAPI';
import { checkAPIResponse } from "../../Utils/APIResponseUtils";
import { toast } from 'sonner';
import { useCurrency } from "../../Hooks/useCurrency";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "../../Components/UI/tooltip";
import { Button } from "../../Components/UI/button";
import { cn } from '../../Lib/Cn';
import { Search, ArrowLeft, Loader2, History, Star, ChevronsUpDown } from 'lucide-react';
import { Input } from '../../Components/UI/input';
import { thrStageShades, checkUserRights, currentUserHasPermission } from '../../Utils/Utilities';
import { FaUpload } from "react-icons/fa6";
import { Card, CardContent } from "../../Components/UI/card";
import Integrations from "./Integrations";
import ImportThreadsExcel from "./ImportThreadsExcel";
import { SHOW_IMPORT_INQUIRIES, canShowImportInquiries } from "../../Utils/FeatureFlags";
import ThreadEntry from "./ThreadEntry";
import ThreadDropdown from "./AddThreadDropdown";
import { isThrStgExpird } from '../../Utils/Utilities';
import { TbListDetails } from "react-icons/tb";
import { FaCircle } from 'react-icons/fa6';
import useIsMobile from '../../Hooks/useIsMobile';
import ConnectionDetails from '../Connections/ConnectionDetails';
import { handlePhoneClickAction } from "../../Utils/ContactLogUtils";
import { useDesktop } from '../../Components/Common/IsDesktop';
import TemplateDialog from '../../Components/Common/TemplateDialog';
import { DatePicker } from "../../Components/Common/DatePicker";
import { FaExpandAlt } from "react-icons/fa";

const layoutOptions = [
	{ id: "Vertical", name: "Vertical View" },
	{ id: "Horizontal", name: "Horizontal View" },
];

const KanbanThreads = () => {
	const location = useLocation();

	const currencySymbol = useCurrency();
	const isDesktop = useDesktop();

	const data = useSelector((state) => state.threadData.data);
	const { loading, error } = useSelector((state) => state.threadData);
	const threadTypes = useSelector(getActiveThreadTypesArr);
	const threadStagesData = useSelector((state) => state.masterData?.threadStages);
	const users = useSelector(getUsersArr);
	const tasksData = useSelector((state) => state.taskData.data) || [];
	const connectionData = useSelector((state) => state.connectionData.data);
	const isMobile = useIsMobile()
	const stageRefs = useRef({});

	const [selectedFilters, setSelectedFilters] = useState({
		types: "",
		connID: "all",
		assignedTo: "all",
		category: "all",
		starOnly: false,
		selectDate: false,
		selectedDate: null,
		dateRange: "all",
		otherMonth: "all",
		otherQuarter: "all",
		otherYear: "all",
		fromDate: null,
		toDate: null
	});
	const [stageInfo, setStageInfo] = useState([]);
	const [kanbanStages, setKanbanStages] = useState([]);
	const [threadsByStage, setThreadsByStage] = useState({});
	const [availableCategories, setAvailableCategories] = useState([]);
	const [selectedThread, setSelectedThread] = useState({});
	const [draggedThreadID, setDraggedThreadID] = useState(null);
	const [isThreadDetailsOpen, setIsThreadDetailsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [layoutOption, setLayoutOption] = useState("Vertical");
	const [isConnDetailOpen, setIsConnDetailOpen] = useState(false);
	const [selectedConnID, setSelectedConnID] = useState(0);
	const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
	const [activeConnection, setActiveConnection] = useState(null);
	const [isVerticalLayout, setIsVerticalLayout] = useState(true);
	const [threadEntryOpen, setThreadEntryOpen] = useState(false);
	const [selectedThreadTypeFromDropdown, setSelectedThreadTypeFromDropdown] = useState(null);
	const [orderImportOpen, setOrderImportOpen] = useState(false);
	const [integrationsOpen, setIntegrationsOpen] = useState(false);
	const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
	const navigate = useNavigate();

	const userRights = checkUserRights();
	const [connData, setConndata] = useState([]);

	// Helper function to check if a date falls within date range filter
	const checkDateRangeFilter = (threadDate) => {
		if (!threadDate) return true;

		const currDT = new Date(threadDate);
		const todayDT = new Date();
		todayDT.setHours(0, 0, 0, 0);
		currDT.setHours(0, 0, 0, 0);

		switch (selectedFilters.dateRange) {
			case "Today":
				return currDT.getTime() === todayDT.getTime();
			case "Yesterday":
				const yest = new Date(todayDT);
				yest.setDate(todayDT.getDate() - 1);
				return currDT.getTime() === yest.getTime();
			case "Last 7 days":
				const last7 = new Date(todayDT);
				last7.setDate(todayDT.getDate() - 7);
				return currDT >= last7 && currDT <= todayDT;
			case "Last month":
				const lastMonth = new Date(todayDT);
				lastMonth.setMonth(todayDT.getMonth() - 1);
				return currDT.getMonth() === lastMonth.getMonth() && currDT.getFullYear() === lastMonth.getFullYear();
			case "This Month":
				return currDT.getMonth() === todayDT.getMonth() && currDT.getFullYear() === todayDT.getFullYear();
			case "Other Month":
				if (selectedFilters.otherMonth === "all") return true;
				// Parse the month string (e.g., "January 2025" or "01/2025")
				const monthParts = selectedFilters.otherMonth.split(" ");
				if (monthParts.length === 2) {
					const monthYear = new Date(`${monthParts[0]} 1, ${monthParts[1]}`);
					return currDT.getMonth() === monthYear.getMonth() && currDT.getFullYear() === monthYear.getFullYear();
				}
				return true;
			case "Other Quarter":
				if (selectedFilters.otherQuarter === "all") return true;
				// Parse quarter string (e.g., "Q1 2025")
				const quarterParts = selectedFilters.otherQuarter.match(/Q(\d) (\d{4})/);
				if (quarterParts) {
					const quarter = parseInt(quarterParts[1]);
					const year = parseInt(quarterParts[2]);
					const month = currDT.getMonth();
					const threadYear = currDT.getFullYear();
					const threadQuarter = Math.floor(month / 3) + 1;
					return threadQuarter === quarter && threadYear === year;
				}
				return true;
			case "Other Year":
				if (selectedFilters.otherYear === "all") return true;
				return currDT.getFullYear() === parseInt(selectedFilters.otherYear);
			default:
				return true;
		}
	};

	const getExecutiveName = (userID) => {
		if (!userID) return "-";
		const user = users.find((user) => user.userID === Number(userID));
		return user ? user.name : "-";
	};

	// Extract unique categories from threads
	const getUniqueCategoriesFromThreads = (threadsList) => {
		const categories = new Set();
		threadsList.forEach(thread => {
			const category = thread.connectionData?.category;
			if (category && category.trim() !== "") {
				categories.add(category);
			}
		});
		return Array.from(categories).sort();
	};

	const handleThreadTypeSelect = (threadTypeID) => {
		setSelectedThreadTypeFromDropdown(threadTypeID);
		setThreadEntryOpen(true);
	};

	const getStageInfo = (typeID, stageID) => {
		const thread = stageInfo.find(thread => thread.TID === typeID);
		if (!thread) return null;
		const stage = thread.Stages.find(stage => stage.SID === stageID);
		if (!stage) return null;
		return { SID: stage.SID, name: stage.S };
	};

	// Process connection data for filter
	useEffect(() => {
		if (!Array.isArray(connectionData)) {
			setConndata([]);
			return;
		}
		// OPTIMIZE_TODO: Avoid converting variable names.
		const filtered = connectionData
			.filter(item => item?.IsCold !== 1 && item?.IsCold !== true)
			.map(item => ({
				connID: item.ConnID,
				bizName: item.BizName || "",
				contactName: item.ContactName || ""
			}))
			.sort((a, b) => (a.bizName || "").localeCompare(b.bizName || ""));
		setConndata(filtered);
	}, [connectionData]);

	// Calculate oldest date for filter options
	const oldestSinceDT = useMemo(() => {
		if (!connectionData || connectionData.length === 0) return new Date();

		const dates = connectionData
			.map((conn) => {
				const dateStr = conn.SinceDT || conn.CreatedDT;
				return dateStr ? new Date(dateStr) : null;
			})
			.filter((d) => d !== null && !isNaN(d));

		if (dates.length === 0) return new Date();

		return new Date(Math.min(...dates));
	}, [connectionData]);

	// Load thread stages configuration
	const filteredThreads = useMemo(() => {
		if (!data || !Array.isArray(data)) {
			return [];
		}

		const term = searchTerm.trim().toLowerCase();

		// Filter to only include active threads (StatusID 0)
		const activeThreads = data.filter(thread => thread.StatusID === 0);

		// Apply filters and process only the active threads
		const filtered = activeThreads.filter((thread) => {
			// Exclude threads from cold contacts
			// 2026-01-28: Replaced with connectionData from thread object itself:
			/*const threadConnection = connectionData?.find(conn => conn.ConnID === thread.ConnID);
			if (threadConnection && (threadConnection.IsCold === 1 || threadConnection.IsCold === true)) {
				return false;
			}*/
			if (thread.connectionData?.IsCold === 1 || thread.connectionData?.IsCold === true) {
				return false;
			}

			// ... existing filter logic (types, connID, assignedTo) ...
			if (selectedFilters.types && selectedFilters.types !== "") {
				if (!selectedFilters.types.toString().includes(thread.TypeID.toString())) {
					return false;
				}
			}

			// Connection filter
			if (selectedFilters.connID && selectedFilters.connID !== "all") {
				if (thread.ConnID.toString() !== selectedFilters.connID.toString()) {
					return false;
				}
			}

			// Assigned To filter
			if (selectedFilters.assignedTo && selectedFilters.assignedTo !== "all") {
				if (selectedFilters.assignedTo === "unassigned") {
					if (thread.POCUserID) return false;
				} else {
					const threadUserID = thread.POCUserID ? Number(thread.POCUserID) : null;
					const selectedUserID = Number(selectedFilters.assignedTo);
					if (threadUserID !== selectedUserID) return false;
				}
			}

			// Category filter
			if (selectedFilters.category && selectedFilters.category !== "all") {
				const threadCategory = thread.connectionData?.Segment || "";
				if (threadCategory !== selectedFilters.category) {
					return false;
				}
			}

			// Star filter
			if (selectedFilters.starOnly && !thread.IsStar) {
				return false;
			}

			// Select Date filter
			if (selectedFilters.selectDate && selectedFilters.selectedDate) {
				const threadDateStr = thread.SinceDT ? thread.SinceDT.split('T')[0] : "";
				const selectedDateStr = selectedFilters.selectedDate;
				if (threadDateStr !== selectedDateStr) {
					return false;
				}
			}

			// Period/Date Range filter
			if (!checkDateRangeFilter(thread.SinceDT)) {
				return false;
			}

			// Custom date range filter (from/to dates when a month is selected)
			if (selectedFilters.dateRange === "Other Month" && selectedFilters.otherMonth !== "all") {
				if (selectedFilters.fromDate || selectedFilters.toDate) {
					const threadDate = new Date(thread.SinceDT);
					threadDate.setHours(0, 0, 0, 0);

					if (selectedFilters.fromDate) {
						const fromDate = new Date(selectedFilters.fromDate);
						fromDate.setHours(0, 0, 0, 0);
						if (threadDate < fromDate) return false;
					}

					if (selectedFilters.toDate) {
						const toDate = new Date(selectedFilters.toDate);
						toDate.setHours(0, 0, 0, 0);
						if (threadDate > toDate) return false;
					}
				}
			}

			// Custom date range filter (from/to dates when This Month is selected)
			if (selectedFilters.dateRange === "This Month") {
				if (selectedFilters.fromDate || selectedFilters.toDate) {
					const threadDate = new Date(thread.SinceDT);
					threadDate.setHours(0, 0, 0, 0);

					if (selectedFilters.fromDate) {
						const fromDate = new Date(selectedFilters.fromDate);
						fromDate.setHours(0, 0, 0, 0);
						if (threadDate < fromDate) return false;
					}

					if (selectedFilters.toDate) {
						const toDate = new Date(selectedFilters.toDate);
						toDate.setHours(0, 0, 0, 0);
						if (threadDate > toDate) return false;
					}
				}
			}

			return true;
		});

		// ... rest of the processing and search logic ...

		// Process the filtered threads
		const processed = filtered.map(item => {
			// ... existing mapping logic (tasks, connectionData, etc.) ...

			// TODO_OPTIMIZE: Avoid converting variable names, ternary operators,
			const currentConn = {
				bizName: item.bizName ?? item.connectionData?.BizName,
				contactName: item.contactName ?? item.connectionData?.ContactName,
				cc: item.cc ?? item.connectionData?.CC,
				mobile: item.mobile ?? item.connectionData?.Mobile,
				email: item.email ?? item.connectionData?.Email,
				lastRecDT: item.lastRec ?? item.connectionData?.LastRecDT,
				category: item.connectionData?.Segment || "",
			};

			// Get tasks for this thread from tasksData
			const currThreadTasks = tasksData.filter((task) => {
				if (!task.ThreadIDs) return false;
				const threadIDs = task.ThreadIDs.split(",").map((id) => id.trim());
				return threadIDs.includes(item.ThreadID.toString());
			});

			// Find next task (earliest DueDT) with better null handling
			let nextTask = null;
			if (currThreadTasks.length > 0) {
				nextTask = currThreadTasks.reduce((earliest, task) => {
					if (!task.DueDT) return earliest;
					const taskDate = new Date(task.DueDT);
					const earliestDate = earliest ? new Date(earliest.DueDT) : null;
					return !earliestDate || taskDate < earliestDate ? task : earliest;
				}, null);
			}

			return {
				threadID: item.ThreadID,
				typeID: item.TypeID,
				agencyID: item.AgencyID,
				connID: item.ConnID,
				mainProduct: item.MainProduct,
				mainQty: item.MainQty,
				model: item.Model,
				pocUserID: item.POCUserID,
				pocUserName: getExecutiveName(item.POCUserID),
				statusID: item.StatusID,
				dueDT: item.DueDT,
				dueDTF: getTableDateOrWeekday(item.DueDT),
				dueDTColor: getColorClass(getTaskColor(item.DueDT)),
				sinceDT: item.SinceDT,
				sinceDTF: getTableDateOrWeekday(item.SinceDT),
				reference: item.Reference,
				bizProspect: item.BizProspect,
				currStageID: item.CurrStageID,
				source: item.Source,
				stageInfo: getStageInfo(item.TypeID, item.CurrStageID),
				nextTaskDT: nextTask?.DueDT || null,
				nextTaskDTColor: nextTask ? getTaskColor(nextTask.DueDT) : "",
				nextTaskDTF: nextTask ? getTableDateOrWeekday(nextTask.DueDT) : "Not Set",
				nextTaskNote: nextTask?.Description || "",
				nextTaskTyp: nextTask?.TaskType || "",
				lastStageChgDT: item?.LastStageChgDT || null,
				lastRecordDT: currentConn?.lastRecDT,
				lastRecordDTColor: getLastDateColor(currentConn?.lastRecDT),
				lastRecordDTF: getTableDateOrWeekday(currentConn?.lastRecDT),
				lastRecordDTAgo: timeAgo(currentConn?.lastRecDT),
				notes: item.Notes,
				connectionData: {
					bizName: currentConn?.bizName,
					contactName: currentConn?.contactName,
					cc: currentConn?.cc,
					mobile: currentConn?.mobile,
					email: currentConn?.email,
					tasks: !isInqOnlyMode ? currThreadTasks.map(task => ({
						TIDs: task.ThreadIDs,
						DT: task.DueDT,
						Txt: task.Description,
						Typ: task.TaskType,
						Pri: task.Priority,
					})) : (item?.connectionData?.Tasks ? JSON.parse(item.connectionData.Tasks) : [])?.filter((task) => task.Sts !== 0 && task.Sts !== 2) || [],
					category: currentConn?.category || "",
				},
				feedbackNotes: item.FdbkNotes,
				feedbackScore: item.FdbkScore,
				failureReason: item.FailureReason,
				isStar: item.IsStar === 1 || item.IsStar === true,
			};
		});

		const searched = term ? processed.filter(thread => {
			// ... existing search logic ...
			if (term === "0") {
				return Number(thread.bizProspect) === 0;
			}

			const fieldsToSearch = [
				thread.connectionData?.contactName,
				thread.pocUserName,
				thread.mainProduct,
				thread.connectionData?.bizName,
				thread.connectionData?.email,
				thread.connectionData?.mobile,
				thread.bizProspect?.toString(),
				thread.nextTaskTyp,
				thread.nextTaskNote,
				thread.source,
				thread.Source,
				thread.src,
				thread.Src,
			];

			return fieldsToSearch
				.filter(Boolean)
				.some((field) => field.toString().toLowerCase().includes(term));
		}) : processed;

		return searched;
	}, [data, selectedFilters, searchTerm, users, stageInfo, tasksData]);

	// Update available categories from filtered threads
	useEffect(() => {
		const categories = getUniqueCategoriesFromThreads(filteredThreads);
		setAvailableCategories(categories);
	}, [filteredThreads]);

	useEffect(() => {
		if (threadStagesData) {
			setStageInfo(threadStagesData);
		}
	}, [threadStagesData]);

	// Process stages for selected thread type
	useEffect(() => {
		if (!stageInfo.length || !selectedFilters.types || selectedFilters.types === "" || selectedFilters.types === "all") {
			setKanbanStages([]);
			return;
		}

		const selectedTypeID = parseInt(selectedFilters.types);
		const stages = stageInfo.find(
			(thread) => thread.TID === selectedTypeID
		)?.Stages.map((stage) => ({
			id: stage.SID,
			name: stage.S
		})) || [];

		setKanbanStages(stages);
	}, [stageInfo, selectedFilters.types]);

	// Group filtered threads by stage
	useEffect(() => {
		if (!filteredThreads.length || !kanbanStages.length) {
			setThreadsByStage({});
			return;
		}

		const grouped = {};
		kanbanStages.forEach(stage => {
			const stageThreads = filteredThreads.filter(thread =>
				thread.currStageID === stage.id
			);

			// Sort by sinceDT - newest first (descending order)
			const sortedThreads = stageThreads.sort((a, b) => {
				const lowerDate = a.sinceDT ? new Date(a.sinceDT).getTime() : 0;
				const upperDate = b.sinceDT ? new Date(b.sinceDT).getTime() : 0;
				return upperDate - lowerDate;
			});

			grouped[stage.id] = sortedThreads;
		});

		setThreadsByStage(grouped);
	}, [filteredThreads, kanbanStages]);

	// Read thread type from URL on component mount
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const threadTypeFromUrl = searchParams.get('tid');

		if (threadTypeFromUrl) {
			setSelectedFilters(prev => ({ ...prev, types: threadTypeFromUrl }));
		} else {
			// Set to empty/default when no tid parameter
			setSelectedFilters(prev => ({ ...prev, types: "" }));
		}
	}, [location.search]);

	// loading most common thread type with most threads if none selected
	useEffect(() => {
		// Check if tid is present in the URL
		const searchParams = new URLSearchParams(location.search);
		const threadTypeFromUrl = searchParams.get('tid');

		// Only set default thread type if no tid is provided in the URL
		if (!threadTypeFromUrl && data && data.length > 0 && selectedFilters.types === "") {
			const typeCounts = {};
			data.forEach(thread => {
				typeCounts[thread.TypeID] = (typeCounts[thread.TypeID] || 0) + 1;
			});

			const typeCountsArray = Object.entries(typeCounts);
			if (typeCountsArray.length > 0) {
				const mostCommonType = typeCountsArray.reduce((a, b) =>
					a[1] > b[1] ? a : b
				)[0];

				setSelectedFilters(prev => ({ ...prev, types: mostCommonType.toString() }));
			}
		}
	}, [data, selectedFilters.types, location.search]);


	// Add this helper function to determine if a stage move is valid
	const isValidStageMove = useCallback((currentStageID, targetStageID) => {
		if (!kanbanStages.length) return false;

		// Find the index of current and target stages
		const currentStageIndex = kanbanStages.findIndex(stage => stage.id === currentStageID);
		const targetStageIndex = kanbanStages.findIndex(stage => stage.id === targetStageID);

		// Check if both stages exist and are different
		if (currentStageIndex === -1 || targetStageIndex === -1 || currentStageIndex === targetStageIndex) {
			return false;
		}

		// Allow all moves (forward or backward, including to first stage)
		return true;
	}, [kanbanStages]);

	// Handle row click to open thread details
	const handleRowClick = (thread) => {
		if (isInqOnlyMode) {
			setSelectedConnID(thread?.connID);
			setSelectedThread(thread);
			setIsConnDetailOpen(true);
		}
		else {
			setSelectedThread(thread);
			setIsThreadDetailsOpen(true);
		}
	};

	// Handle toggle star for a thread
	const handleToggleStar = async (thread, event) => {
		event?.stopPropagation();
		const newStarStatus = !thread.isStar;

		setThreadsByStage(prevThreadsByStage => {
			const newThreadsByStage = { ...prevThreadsByStage };
			Object.keys(newThreadsByStage).forEach(stageId => {
				newThreadsByStage[stageId] = newThreadsByStage[stageId].map(t =>
					t.threadID === thread.threadID ? { ...t, isStar: newStarStatus } : t
				);
			});
			return newThreadsByStage;
		});

		try {
			const response = await toggleThreadStarAPI(thread, newStarStatus);
			if (checkAPIResponse(response)) {
				toast.success(newStarStatus ? `${isInqOnlyMode ? "Inquiry marked as important" : "Thread marked as important"}` : `${isInqOnlyMode ? "Inquiry unmarked as important" : "Thread unmarked as important"}`);
			}
		} catch (error) {
			setThreadsByStage(prevThreadsByStage => {
				const newThreadsByStage = { ...prevThreadsByStage };
				Object.keys(newThreadsByStage).forEach(stageId => {
					newThreadsByStage[stageId] = newThreadsByStage[stageId].map(t =>
						t.threadID === thread.threadID ? { ...t, isStar: !newStarStatus } : t
					);
				});
				return newThreadsByStage;
			});
			toast.error(error.message || "Failed to star inquiry.");
		}
	};


	// Function to handle drag end
	const handleDragEnd = useCallback(async (event) => {
		const { active, over } = event;

		if (!currentUserHasPermission('ThrActions')) {
			toast.error("Permission Denied", {
				description: "You do not have permission to update stages.",
			});
			return;
		}


		if (!over) return;

		const activeThreadID = active.id;
		const newStageID = parseInt(over.id);

		// Find the thread being moved
		const activeThread = filteredThreads.find(thread => thread.threadID === activeThreadID);

		if (!activeThread || activeThread.currStageID === newStageID) return;

		// Check if this is a valid move (to any different valid stage)
		if (!isValidStageMove(activeThread.currStageID, newStageID)) {
			// Show error message for invalid move
			toast.error("Invalid Move", {
				description: "Thread cannot be moved to the same stage or invalid stage.",
			});
			return;
		}

		const originalStageID = activeThread.currStageID;

		// Optimistic update - immediately update UI
		setThreadsByStage(prevThreadsByStage => {
			const newThreadsByStage = { ...prevThreadsByStage };

			// Remove thread from old stage
			newThreadsByStage[originalStageID] = newThreadsByStage[originalStageID]?.filter(
				thread => thread.threadID !== activeThreadID
			) || [];

			// Add thread to new stage
			const updatedThread = { ...activeThread, currStageID: newStageID };
			newThreadsByStage[newStageID] = [...(newThreadsByStage[newStageID] || []), updatedThread];

			return newThreadsByStage;
		});

		try {
			// Call the API with the thread data and new stage
			await updateThreadStageAPI(activeThread, newStageID, false);

			toast.success(`${isInqOnlyMode ? "Inquiry" : "Thread"} stage updated.`, {
				description: `${isInqOnlyMode ? "Inquiry" : "Thread"} moved from ${getStageInfo(activeThread.typeID, activeThread.currStageID).name} to stage 
					${getStageInfo(activeThread.typeID, newStageID).name}.`,
			});


		} catch (error) {
			// Revert the optimistic update on API failure
			setThreadsByStage(prevThreadsByStage => {
				const revertedThreadsByStage = { ...prevThreadsByStage };

				// Remove thread from new stage
				revertedThreadsByStage[newStageID] = revertedThreadsByStage[newStageID]?.filter(
					thread => thread.threadID !== activeThreadID
				) || [];

				// Add thread back to original stage
				const revertedThread = { ...activeThread, currStageID: originalStageID };
				revertedThreadsByStage[originalStageID] = [...(revertedThreadsByStage[originalStageID] || []), revertedThread];

				return revertedThreadsByStage;
			});

			// Set error state for user feedback
			toast.error("Failed to update thread stage", {
				description: `${error.message}`,
			});

		}
	}, [filteredThreads, isValidStageMove]);

	// Add sensors
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 3,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleSearchChange = useCallback((e) => {
		setSearchTerm(e.target.value);
	}, []);

	const handleLayoutChange = useCallback((value) => {
		setLayoutOption(value);
		setIsVerticalLayout(value === "Vertical");
	}, []);

	// Draggable ThreadCard Component
	const DraggableThreadCard = ({ thread }) => {
		const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
			id: thread.threadID,
		});

		const style = useMemo(() => ({
			transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
			opacity: isDragging ? 0.7 : 1,
			zIndex: isDragging ? 1000 : 'auto',
			willChange: isDragging ? 'transform' : 'auto',
			backfaceVisibility: 'hidden',
			perspective: 1000,
		}), [transform, isDragging]);

		return (
			<div
				ref={setNodeRef}
				style={style}
				{...listeners}
				{...attributes}
				className={`cursor-grab ${isDragging ? 'cursor-grabbing' : ''}`}
			>
				<ThreadCard
					thread={thread}
				/>
			</div>
		);
	};

	// Thread Card Component
	const ThreadCard = ({ thread }) => {

		let leftColor = thrStageShades(thread.stageInfo.SID - 1, isInqOnlyMode ? 2 : thread.typeID)
		let sortedTask = thread?.connectionData?.tasks?.sort((task1, task2) => {
			const date1 = new Date(task1.DT);
			const date2 = new Date(task2.DT);

			// Handle invalid dates by pushing them to the end
			if (isNaN(date1.getTime())) return 1;
			if (isNaN(date2.getTime())) return -1;

			// Sort ascending (earliest date first)
			return date1 - date2;
		}) || [];

		let inqOnlyNextTaskDT = null
		if (sortedTask.length != 0) {
			inqOnlyNextTaskDT = sortedTask[0]?.DT
		}

		const isThreadStuck = thread.lastStageChgDT && isThrStgExpird(thread.lastStageChgDT, thread.typeID, thread.currStageID, stageInfo);

		const cardContent = (
			<div
				className={`bg-gray-100 sahi-radius shadow-sm border border-gray-200 f-col justify-between hover:cursor-pointer relative overflow-hidden 
							
							`}
				onClick={() => handleRowClick(thread)}
			>
				{/* Left Color Border */}
				<div
					className="absolute left-0 top-0 bottom-0 w-1"
					style={{ backgroundColor: leftColor }}
				/>
				{/* === Header Section === */}
				<div className="f-col gap-1 p-3 truncate pt-4 pl-4">
					<div className="flex items-center justify-between max-w-full">
						<p className="font-semibold text-text-color text-md truncate"
							title={thread.connectionData?.bizName || thread.connectionData?.contactName}
						>
							{thread.connectionData?.bizName || thread.connectionData?.contactName}
						</p>

						<div className="flex items-center gap-2 shrink-0">
							<Star
								className={`w-4 h-4 transition-colors cursor-pointer ${thread.isStar ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`}
								onClick={(e) => {
									e.stopPropagation();
									handleToggleStar(thread, e);
								}}
							/>
							{thread.lastStageChgDT &&
								isThrStgExpird(thread.lastStageChgDT, thread.typeID, thread.currStageID, stageInfo) && (
									<FaCircle className="text-sahi-red text-[10px]" />
								)}
						</div>
					</div>
					<div className="f-row justify-between gap-1">
						<div className="f-col items-start justify-start flex-1 min-w-0">
							<div className="f-col min-w-0 gap-1 max-w-full">
								<p className="text-sm text-sub-text-color mt-1 truncate">
									{(() => {
										const text = thread?.connectionData?.bizName
											? thread.connectionData.contactName
											: "-";

										return text.length > 23 ? `${text.slice(0, 23)}...` : text;
									})()}
								</p>

								{/* Product Name */}
								{thread.mainProduct && (
									<p className="text-sm text-sub-text-color mt-1 truncate">
										{thread.mainProduct.length > 25
											? `${thread.mainProduct.slice(0, 25)}...`
											: thread.mainProduct}

									</p>
								)}
							</div>
						</div>


						<div className="f-col gap-2 justify-end items-end text-right shrink-0">

							{/* Amount */}
							<span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis font-semibold text-sub-text-color text-right">
								{
									thread.bizProspect === 0 ? '' : (
										<FormatAmount
											amount={thread.bizProspect}
											currency={currencySymbol}
											showCurr={true}
										/>
									)
								}
							</span>


							{/* Received Date */}
							<span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis text-sub-text-color text-right">
								Recd: <strong>{thread.sinceDTF || "-"}</strong>
							</span>


							{/* Due Date */}
							{/* {thread.dueDT && (
								<span className="text-sm text-sub-text-color text-right">
									Due: <strong className={thread.dueDTColor}>{thread.dueDTF}</strong>
								</span>
							)} */}
						</div>
					</div>
				</div>

				{/* === Footer Section === */}
				<div className="flex justify-between items-center text-sm border-t bg-sahi-dark-grey rounded-b-md border-100 w-full p-2 px-3 pl-4">
					<div className="flex flex-wrap gap-3 items-center">
						{/* Next Task */}
						<span className=" whitespace-nowrap text-sub-text-color">
							Next:{" "}
							{thread.nextTaskDT && !isInqOnlyMode ? (
								<>
									<strong className="" style={{ color: thread.nextTaskDTColor }}>
										{thread.nextTaskDTF}
									</strong>
									{thread.nextTaskTyp && ` (${thread.nextTaskTyp})`}
								</>
							) : (isInqOnlyMode && inqOnlyNextTaskDT ? (
								<>
									<strong className="" style={{ color: getTaskColor(inqOnlyNextTaskDT) || '' }}>
										{getTableDateOrWeekday(inqOnlyNextTaskDT) || 'Not Set'}
									</strong>
								</>) :
								(<>
									Not Set
								</>
								))}
						</span>
					</div>

					{/* Contact Actions */}
					<div className="flex gap-2 text-lg justify-end">
						{thread.connectionData?.mobile && (

							<button
								className="h-6 w-6 flex items-center justify-center rounded-md hover:shadow-sm cursor-pointer transform-gpu transition-transform  duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-blue-light"
								onClick={(e) => {
									e.stopPropagation();
									handlePhoneClickAction(thread.connectionData, isDesktop, null, thread.connID);
								}}
							>
								<MdPhone className="w-4.5 h-4.5 text-black" />
							</button>
						)}
						{thread.connectionData?.mobile && (
							<button
								className="h-6 w-6 flex items-center justify-center rounded-md hover:shadow-sm cursor-pointer transform-gpu transition-transform  duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-green-light"
								onClick={(e) => {
									e.stopPropagation();
									setTemplateDialogOpen(true);
									setActiveConnection({
										connectionData: thread.connectionData,
										connID: thread.connID,
										currStageID: thread.currStageID
									});
								}}
							>
								<BsWhatsapp className="w-4.5 h-4.5 text-sahi-green" />
							</button>
						)}
					</div>
				</div>
			</div>
		);

		if (isThreadStuck) {
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						{cardContent}
					</TooltipTrigger>
					<TooltipContent side="top">
						<div className="flex items-center gap-2">
							<FaCircle className="text-sahi-red text-[10px]" />
							<span className="text-sm font-medium">
								{thread.typeID === 1 ? 'Inquiry' : 'Thread'} stuck at this stage for too long!
							</span>
						</div>
					</TooltipContent>
				</Tooltip>
			);
		}

		return cardContent;
	}

	const StageColumnVertical = ({ stage, threads, threadTypes, selectedFilters, isVertical }) => {
		const { setNodeRef, isOver } = useDroppable({
			id: stage.id,
		});

		// Ref to track the specific stage wrapper
		const wrapperRef = useRef(null);

		useEffect(() => {
			if (!isVertical || !wrapperRef.current) {
				if (wrapperRef.current) wrapperRef.current.style.height = "auto";
				return;
			}

			const equalizeHeights = () => {
				if (typeof window === 'undefined') return;

				// Collect all stage wrappers
				const wrappers = Array.from(document.querySelectorAll(".stage-wrapper"));
				if (!wrappers.length) return;

				// Reset heights to measure natural content height
				wrappers.forEach((wrapper) => {
					wrapper.style.height = "auto";
				});

				// Measure maximum height using getBoundingClientRect for accuracy
				let maxHeight = 0;
				wrappers.forEach((wrapper) => {
					const rect = wrapper.getBoundingClientRect();
					const height = rect.height;
					if (height > maxHeight) maxHeight = height;
				});

				// Apply the maximum height to all wrappers
				wrappers.forEach((wrapper) => {
					wrapper.style.height = `${maxHeight}px`;
				});
			};

			// Initial measurement with requestAnimationFrame
			let raf = requestAnimationFrame(() => {
				equalizeHeights();
				// Optional: Add a small timeout to ensure content is fully rendered
				setTimeout(equalizeHeights, 100); // Adjust timeout if needed
			});

			// Debounced resize handler
			const debounce = (func, delay) => {
				let timeoutID;
				return (...args) => {
					clearTimeout(timeoutID);
					timeoutID = setTimeout(() => func(...args), delay);
				};
			};
			const handleResize = debounce(equalizeHeights, 100);

			// Add window resize listener
			window.addEventListener("resize", handleResize);

			// Observe content changes with ResizeObserver
			let resizeObserver;
			if (window.ResizeObserver) {
				resizeObserver = new ResizeObserver(() => {
					if (raf) cancelAnimationFrame(raf);
					raf = requestAnimationFrame(equalizeHeights);
				});
				wrapperRef.current && resizeObserver.observe(wrapperRef.current);
			}

			// Cleanup
			return () => {
				if (raf) cancelAnimationFrame(raf);
				window.removeEventListener("resize", handleResize);
				if (resizeObserver) {
					resizeObserver.disconnect();
				}
				if (wrapperRef.current) {
					wrapperRef.current.style.height = "auto";
				}
			};
		}, [isVertical, threads]); // Re-run when layout or threads change

		// Visual feedback for drop zones
		const getDropZoneClass = () => {
			if (!isOver || !draggedThreadID) return "bg-white";
			const draggedThread = filteredThreads.find((t) => t.threadID === draggedThreadID);
			if (!draggedThread) return "bg-white";
			if (draggedThread.currStageID === stage.id) {
				return "bg-white border-2 border-gray-200";
			}
			if (isValidStageMove(draggedThread.currStageID, stage.id)) {
				return "border-2 border-sahi-primary-color";
			} else {
				return "border-2 border-red-300";
			}
		};

		const containerClass = `shadow-sm sahi-radius p-4 py-4 lg:p-2 transition-colors duration-200 ${getDropZoneClass()}`;

		return (
			<div ref={wrapperRef} className={`w-full stage-wrapper ${isVertical ? "flex flex-col" : ""}`}>
				<div className="px-4 py-3 pl-0 flex justify-between items-start">
					<div className="flex gap-1">
						<h3 className="font-semibold text-text-color text-xl">{stage.name}</h3>
						<span className="text-text-color text-xl font-normal">
							{threads.length > 0 && `(${threads.length})`}
						</span>
					</div>
				</div>
				<div
					ref={setNodeRef}
					className={`${containerClass} stage-container ${isVertical ? "flex-1 " : ""}`}
					style={isVertical ? {} : {}}
				>
					{threads.length === 0 ? (
						<div className={`relative text-sub-text-color ${isVertical ? "" : "py-20"}`}>
							<div className={isVertical ? "" : "absolute top-0 left-0"}>
								{(() => {
									const selectedThreadType = threadTypes.find(
										(t) => t.TID.toString() === selectedFilters.types
									);
									if (!selectedThreadType) {
										return "No threads in this stage.";
									}
									return `No ${selectedThreadType.Typ} in this stage.`;
								})()}
							</div>
						</div>
					) : (
						<div
							className={isVertical ? "flex flex-col gap-4" : "grid gap-4 w-full"}
							style={
								isVertical
									? {}
									: {
										gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
									}
							}
						>
							{threads.map((thread) => (
								<DraggableThreadCard
									key={thread.threadID}
									thread={thread}
									threadTypes={threadTypes}
									selectedFilters={selectedFilters}
									isVertical={isVertical}
								/>
							))}
						</div>
					)}
				</div>
			</div>
		);
	};


	const showSearch = !!selectedFilters.types && selectedFilters.types !== "all";
	if (loading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Loader2 className="h-8 w-8 animate-spin text-sahi-primary-color mr-3" />
				<span className="text-text-color">Loading Kanban...</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className="p-6">
				<Card className="border-sahi-red bg-red-50">
					<CardContent className="p-4">
						<p className="text-sahi-red font-medium">{error}</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Modified return JSX
	return (
		<TooltipProvider>
			<div className="overflow-hidden scrollbar-hide">
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					modifiers={[restrictToWindowEdges]}
					onDragStart={(event) => setDraggedThreadID(event.active.id)}
					onDragEnd={(event) => {
						handleDragEnd(event);
						setDraggedThreadID(null);
					}}
				>
					<div className="space-y-3 lg:space-y-6 ">
						{/* Header */}
						<div className="hidden lg:flex justify-between items-center gap-3">
							<div className="flex items-center gap-4 justify-between w-full">
								<div className="flex flex-col mr-4">
									<h1 className="text-3xl font-bold text-text-color web-header-name">
										Kanban
									</h1>
									{/* {!isMobile &&
									<span className="text-xs mt-1 text-gray-600 leading-relaxed whitespace-nowrap">Track specific requests and potential orders linked to your contacts.</span>} */}
								</div>


								{/* Layout Toggle */}
								{/* <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
								<button
									onClick={() => setIsVerticalLayout(true)}
									className={`p-2 rounded-md transition-colors ${isVerticalLayout
										? 'bg-sahi-primary-color text-white'
										: 'text-gray-700 hover:bg-gray-100'
										}`}
								>
									<Columns className="h-5 w-5" />
								</button>
								<button
									onClick={() => setIsVerticalLayout(false)}
									className={`p-2 rounded-md transition-colors ${!isVerticalLayout
										? 'bg-sahi-primary-color text-white'
										: 'text-gray-700 hover:bg-gray-100'
										}`}
								>
									<Rows className="h-5 w-5" />
								</button>
							</div> */}
								<div className="flex gap-3">
									<div
										className={cn(
											"flex items-center px-3 lg:px-4 h-8 lg:h-10",
											"w-fit max-w-[240px] bg-white text-black rounded-lg border filter-border shadow-sm"
										)}
									>
										<span className="f-row gap-1 whitespace-nowrap text-xs lg:text-sm">
											<span>Total: </span>
											<span className="font-medium">{filteredThreads.length}</span>
										</span>
									</div>

									{showSearch && (
										<div className="flex items-center justify-end flex-grow">
											<div className="relative filter-border">
												<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
												<Input
													placeholder="Search"
													value={searchTerm}
													onChange={handleSearchChange}
													className="pl-10 h-10 w-80 border-none focus:outline-none focus:ring-0 shadow-none"
												/>
											</div>
										</div>
									)}

									{/* Add Thread Buttons */}
									{userRights?.addThr && (
										<>
											{selectedFilters.types && selectedFilters.types !== "" && selectedFilters.types !== "all" && (
												<>
													{canShowImportInquiries() && (
														parseInt(selectedFilters.types) === 1 ? (
															<Integrations
																buttonText="Import"
																threadInfo={{
																	typeID: 1,
																	importType: "inquiry"
																}}
															/>
														) : parseInt(selectedFilters.types) === 2 ? (
															<>
																<Button
																	onClick={() => setOrderImportOpen(true)}
																	variant="additional"
																>
																	<FaUpload className="h-5 w-5" />
																	<p>Import</p>
																</Button>
																<ImportThreadsExcel
																	open={orderImportOpen}
																	onClose={() => setOrderImportOpen(false)}
																	threadInfo={{
																		typeID: 2,
																		importType: "order"
																	}}
																	triggerButton={false}
																/>
															</>
														) : (
															<Integrations
																buttonText="Import"
																threadInfo={{
																	typeID: parseInt(selectedFilters.types),
																	importType: "inquiry"
																}}
															/>
														)
													)}
													<ThreadDropdown
														className="text-base"
														onThreadTypeSelect={handleThreadTypeSelect}
														iconButton={false}
														activeFilteredTypes={selectedFilters.types}
													/>
												</>
											)}
										</>
									)}

									<Button
										className="button-config text-base sahi-primary-button flex items-center py-[6px] px-[10px]"
										onClick={() => navigate('/threads')}
									>
										<TbListDetails className='h-4 w-4' />
										<p>List View</p>
									</Button>
									<Button
										className="button-config text-base sahi-primary-button flex items-center py-[6px] px-[10px]"
										onClick={() => navigate('/threads-detail')}
									>
										<FaExpandAlt className="h-4 w-4" />
										<p>Expand</p>
									</Button>
									{!isInqOnlyMode && selectedFilters.types && selectedFilters.types !== "" && selectedFilters.types !== "all" && (
										<Button
											onClick={() => {
												window.open(`/Web/threads-report?tID=${selectedFilters.types}`, '_blank');
											}}
											variant="secondary"
										>
											<div className="h-6 w-6 flex items-center justify-center transition-all">
												<History className="h-4 w-4" />
											</div>
										</Button>
									)}
									{isInqOnlyMode &&
										<Button
											onClick={() => {
												window.open(`/Web/threads-report?tID=1`, '_blank');
											}}
											variant="secondary"
										>
											<div className="h-6 w-6 flex items-center justify-center transition-all">
												<History className="h-4 w-4" />
											</div>
										</Button>
									}
								</div>
							</div>
						</div>



						{/* Filter + Stage Navigation */}
						<div className="hidden lg:flex flex-wrap items-center justify-start gap-4">

							{/* Layout View Dropdown */}
							<CommonFilter
								label="View"
								options={layoutOptions}
								value={layoutOption}
								onChange={handleLayoutChange}
								displayValue={(val) => val}
								hideAllOption={true}
								searchEnabled={false}
								showClearButton={false}
							/>

							{
								!isInqOnlyMode &&
								<>
									{/* Thread Type Filter */}
									<CommonFilter
										label="Select Thread Type"
										options={threadTypes.map((t) => ({ name: t.Typ, ID: t.TID.toString() }))}
										value={selectedFilters.types}
										onChange={(value) => {
											setSelectedFilters((prev) => ({ ...prev, types: value }));
											if (!value || value === "all") {
												setSearchTerm("");
											}
										}}
										searchEnabled={true}
										displayValue={(val) => {
											if (!val || val === "") return "All Thread Types";
											const selectedType = threadTypes.find((t) => t.TID.toString() === val);
											return selectedType ? selectedType.Typ : "All Thread Types";
										}}
										showAllOptionNum={true}
										showClearButton={false}
										hideAllOption={true}
									/>
								</>
							}


							{/* Contact Filter */}
							<CommonFilter
								label="All Contacts"
								options={connData.map((c) => ({ name: c.bizName || c.contactName, ID: c.connID }))}
								value={selectedFilters.connID}
								onChange={(value) => {
									setSelectedFilters((prev) => ({ ...prev, connID: value }));
								}}
								searchEnabled={true}
								displayValue={(val) => {
									const numVal = Number(val);
									const connection = connData.find((c) => Number(c.connID) === numVal);
									return connection?.bizName || connection?.contactName || "";
								}}
								showAllOption={true}
							/>

							{/* Executive Filter */}
							{!isSmartSellerMode() ? <>
								<CommonFilter
									label="All Executives"
									options={[
										{ name: "Not Assigned", ID: "unassigned" },
										...users.map((u) => ({ name: u.name, ID: u.userID }))
									]}
									value={selectedFilters.assignedTo}
									onChange={(value) => {
										setSelectedFilters((prev) => ({ ...prev, assignedTo: value }));
									}}
									searchEnabled={true}
									displayValue={(val) => {
										if (val === "unassigned") return "Not Assigned";
										return users.find((u) => u.userID === val)?.name;
									}}
									showAllOption={true}
								/></> : null}

							{/* Category Filter */}
							{availableCategories.length > 0 && (
								<CommonFilter
									label="All Categories"
									options={availableCategories.map((cat) => ({ name: cat, ID: cat }))}
									value={selectedFilters.category}
									onChange={(value) => {
										setSelectedFilters((prev) => ({ ...prev, category: value }));
									}}
									searchEnabled={true}
									displayValue={(val) => {
										if (val === "all") return "All Categories";
										return val;
									}}
									showAllOption={true}
								/>
							)}

							{/* Period Filter */}
							<CommonFilter
								label={selectedFilters.dateRange === "all" ? "All Periods" : selectedFilters.dateRange}
								options={getDateRangeOptions().map(option => ({
									name: option,
									ID: option,
								}))}
								value={selectedFilters.dateRange}
								onChange={(value) => {
									setSelectedFilters((prev) => ({
										...prev,
										dateRange: value,
										otherMonth: "all",
										otherQuarter: "all",
										otherYear: "all",
										fromDate: null,
										toDate: null
									}));
								}}
								searchEnabled={false}
								displayValue={(val) => val}
								showAllOption={false}
								hideAllOption={true}
								showClearButton={true}
							/>

							{/* Other Month Filter - shows when "Other Month" is selected */}
							{selectedFilters.dateRange === "Other Month" && (
								<CommonFilter
									label="Select Month"
									options={getPastMonthOptions(oldestSinceDT).map(option => ({
										name: option.name,
										ID: option.ID,
									}))}
									value={selectedFilters.otherMonth}
									onChange={(value) => {
										setSelectedFilters((prev) => ({
											...prev,
											otherMonth: value,
											fromDate: null,
											toDate: null
										}));
									}}
									searchEnabled={true}
									displayValue={(val) => val}
									showAllOption={false}
									hideAllOption={true}
								/>
							)}

							{/* Other Quarter Filter - shows when "Other Quarter" is selected */}
							{selectedFilters.dateRange === "Other Quarter" && (
								<CommonFilter
									label="Select Quarter"
									options={getPastQuarterOptions(oldestSinceDT).map(option => ({
										name: option.name,
										ID: option.ID,
									}))}
									value={selectedFilters.otherQuarter}
									onChange={(value) => {
										setSelectedFilters((prev) => ({
											...prev,
											otherQuarter: value,
											fromDate: null,
											toDate: null
										}));
									}}
									searchEnabled={true}
									displayValue={(val) => val}
									showAllOption={false}
									hideAllOption={true}
								/>
							)}

							{/* Other Year Filter - shows when "Other Year" is selected */}
							{selectedFilters.dateRange === "Other Year" && (
								<CommonFilter
									label="Select Year"
									options={getOtherYearOptions(oldestSinceDT).map(option => ({
										name: option.name,
										ID: option.ID,
									}))}
									value={selectedFilters.otherYear}
									onChange={(value) => {
										setSelectedFilters((prev) => ({
											...prev,
											otherYear: value,
											fromDate: null,
											toDate: null
										}));
									}}
									searchEnabled={true}
									displayValue={(val) => val}
									showAllOption={false}
									hideAllOption={true}
								/>
							)}

							{/* Date Range Pickers - shows when a month is selected (either in dateRange or otherMonth) */}
							{(selectedFilters.dateRange === "Other Month" && selectedFilters.otherMonth !== "all") && (() => {
								const monthParts = selectedFilters.otherMonth.split(" ");
								const monthYear = monthParts.length === 2 ? `${monthParts[0]} ${monthParts[1]}` : null;
								const firstDay = monthParts.length === 2 ? new Date(`${monthParts[0]} 1, ${monthParts[1]}`) : null;
								const lastDay = monthParts.length === 2 ? (() => {
									const date = new Date(`${monthParts[0]} 1, ${monthParts[1]}`);
									date.setMonth(date.getMonth() + 1);
									date.setDate(0);
									return date;
								})() : null;

								return (
									<div className="flex items-center gap-2">
										{/* <span className="text-sm font-medium text-text-color whitespace-nowrap">Date Range:</span> */}
										<DatePicker
											value={selectedFilters.fromDate || ""}
											onChange={(date) => {
												setSelectedFilters((prev) => ({ ...prev, fromDate: date }));
											}}
											placeholder="From"
											disableFuture={false}
											className="!space-y-0 !h-10 w-36"
											minDate={firstDay?.toISOString().split('T')[0]}
											maxDate={lastDay?.toISOString().split('T')[0]}
											disableMonthNavigation={true}
											disableYearNavigation={true}
											defaultMonth={monthYear}
										/>
										<span className="text-text-color">to</span>
										<DatePicker
											value={selectedFilters.toDate || ""}
											onChange={(date) => {
												setSelectedFilters((prev) => ({ ...prev, toDate: date }));
											}}
											placeholder="To"
											disableFuture={false}
											className="!space-y-0 !h-10 w-36"
											minDate={firstDay?.toISOString().split('T')[0]}
											maxDate={lastDay?.toISOString().split('T')[0]}
											disableMonthNavigation={true}
											disableYearNavigation={true}
											defaultMonth={monthYear}
										/>
									</div>
								);
							})()}

							{/* Date Range Pickers for This Month */}
							{selectedFilters.dateRange === "This Month" && (() => {
								const now = new Date();
								const currentMonth = now.getMonth();
								const currentYear = now.getFullYear();
								const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
								const monthYear = `${months[currentMonth]} ${currentYear}`;
								const firstDay = new Date(currentYear, currentMonth, 1);
								const lastDay = new Date(currentYear, currentMonth + 1, 0);

								return (
									<div className="flex items-center gap-2">
										{/* <span className="text-sm font-medium text-text-color whitespace-nowrap">Date Range:</span> */}
										<DatePicker
											value={selectedFilters.fromDate || ""}
											onChange={(date) => {
												setSelectedFilters((prev) => ({ ...prev, fromDate: date }));
											}}
											placeholder="From"
											disableFuture={false}
											className="!space-y-0 !h-10 w-36"
											minDate={firstDay?.toISOString().split('T')[0]}
											maxDate={lastDay?.toISOString().split('T')[0]}
											disableMonthNavigation={true}
											disableYearNavigation={true}
											defaultMonth={monthYear}
										/>
										<span className="text-text-color">to</span>
										<DatePicker
											value={selectedFilters.toDate || ""}
											onChange={(date) => {
												setSelectedFilters((prev) => ({ ...prev, toDate: date }));
											}}
											placeholder="To"
											disableFuture={false}
											className="!space-y-0 !h-10 w-36"
											minDate={firstDay.toISOString().split('T')[0]}
											maxDate={lastDay.toISOString().split('T')[0]}
											disableMonthNavigation={true}
											disableYearNavigation={true}
											defaultMonth={monthYear}
										/>
									</div>
								);
							})()}

							{/* Stage Navigation */}
							{
								<div className="flex gap-4 border-gray-200 flex-1">
									{kanbanStages.map((stage) => {
										const totalAmount = (threadsByStage[stage.id] || []).reduce(
											(sum, thread) => sum + Number(thread.bizProspect || 0),
											0
										);

										return (
											<Button
												key={stage.id}
												onClick={() => {
													if (!isVerticalLayout) {
														stageRefs.current[stage.id]?.scrollIntoView({
															behavior: "smooth",
															block: "start",
														});
													}
												}}
												variant="semidefaultcol"
												className={cn(
													"w-auto bg-white text-black sahi-radius px-3 py-2",
													"filter-border",
													"flex flex-row items-center align-middle",
													isVerticalLayout ? "cursor-default" : "cursor-pointer"
												)}
											>
												<label className={`inline-flex items-center gap-1 ${!isVerticalLayout ? 'cursor-pointer' : ''}`}>
													<div className="ml-1 flex flex-row item s-center gap-1">
														<span className="flex items-center text-sm font-normal text-black gap-1">
															{stage.name}
															<span className="flex items-center text-sm text-text-color font-semibold ">
																<span className="text-xs text-sub-text-color font-medium">(</span>
																{totalAmount === 0 ? (
																	`${currencySymbol} 0`
																) : (
																	<FormatAmount
																		amtClassName="text-sm font-semibold text-text-color"
																		amount={totalAmount}
																		currency={currencySymbol}
																		showCurr={true}
																	/>
																)}
																<span className="text-xs text-sub-text-color font-medium ml-0.5">)</span>
															</span>
														</span>
													</div>
												</label>
											</Button>
										);
									})}
									{/* Star Filter */}
									<label className="flex items-center cursor-pointer select-none">
										<input
											type="checkbox"
											id="starFilter"
											checked={selectedFilters.starOnly}
											onChange={(e) => {
												setSelectedFilters((prev) => ({ ...prev, starOnly: e.target.checked }));
											}}
											className="mr-2 h-4 w-4 rounded border-gray-300 filter-border text-sahi-dark-blue focus:ring-sahi-dark-blue"
										/>
										<span className="text-sm font-medium text-text-color whitespace-nowrap">
											{isInqOnlyMode ? "Star Inquiries" : "Star Threads"}
										</span>
									</label>

									{/* Select Date Filter */}
									{/* <label className="flex items-center cursor-pointer select-none">
									<input
										type="checkbox"
										id="selectDateFilterCheckbox"
										checked={selectedFilters.selectDate}
										onChange={(e) => {
											setSelectedFilters((prev) => ({ ...prev, selectDate: e.target.checked, selectedDate: e.target.checked ? prev.selectedDate : null }));
										}}
										className="mr-2 h-4 w-4 rounded border-gray-300 filter-border text-sahi-dark-blue focus:ring-sahi-dark-blue"
									/>
									<span className="text-sm font-medium text-text-color whitespace-nowrap">Select Date</span>
								</label> */}

									{/* {selectedFilters.selectDate && (
									<div className="flex items-center gap-2">
										<DatePicker
											value={selectedFilters.selectedDate || ""}
											onChange={(date) => {
												// Convert date to YYYY-MM-DD format
												const dateObj = new Date(date);
												const year = dateObj.getFullYear();
												const month = String(dateObj.getMonth() + 1).padStart(2, '0');
												const day = String(dateObj.getDate()).padStart(2, '0');
												const formattedDate = `${year}-${month}-${day}`;
												setSelectedFilters((prev) => ({ ...prev, selectedDate: formattedDate }));
											}}
											placeholder="Select date"
											disableFuture={false}
											className="!space-y-0 !h-10 w-40"
										/>
									</div>	
								)} */}
								</div>
							}
						</div>

						{/* Kanban Board Content */}
						<div className="w-full justify-between overflow-hidden">
							{kanbanStages.length === 0 ? (
								<div>
									<p className="no-records border border-gray-200 shadow-sm sahi-radius">
										{selectedFilters.types === "" || selectedFilters.types === "all"
											? "Please select a thread type to view stages."
											: "No stages found for selected thread type."
										}
									</p>
								</div>
							) : (
								<div className={
									isVerticalLayout
										? "flex gap-4 lg:gap-2 min-h-[600px] w-[calc(100vw-133px)] justify-between"
										: "space-y-6"
								}>
									{kanbanStages.map((stage) => (
										<div
											key={stage.id}
											ref={(el) => (stageRefs.current[stage.id] = el)}
											className={isVerticalLayout ? "flex-shrink-0 min-w-0" : ""}
											style={
												isVerticalLayout
													? {
														width: `calc((100vw - 133px) / ${kanbanStages.length})`,
														maxWidth: `calc((100vw - 165px) / ${kanbanStages.length})`,
														flex: '0 0 auto'
													}
													: {}
											}
										>
											<StageColumnVertical
												stage={stage}
												threads={threadsByStage[stage.id] || []}
												threadTypes={threadTypes}
												selectedFilters={selectedFilters}
												isVertical={isVerticalLayout}
											/>
										</div>
									))}
								</div>
							)}
						</div>

						<ThrDetails
							isOpen={isThreadDetailsOpen}
							onClose={() => setIsThreadDetailsOpen(false)}
							threadID={selectedThread?.threadID}
							initialData={selectedThread}
						/>

						<ThreadEntry
							triggerButton={false}
							threadTypeID={selectedThreadTypeFromDropdown || 1}
							open={threadEntryOpen}
							onOpenChange={(open) => {
								setThreadEntryOpen(open);
								if (!open) {
									setSelectedThreadTypeFromDropdown(null);
								}
							}}
						/>

						{userRights?.addThr && (
							<Integrations
								triggerButton={false}
								open={integrationsOpen}
								onOpenChange={setIntegrationsOpen}
							/>
						)}

						<ConnectionDetails
							connID={selectedConnID}
							isOpen={isConnDetailOpen}
							onClose={() => setIsConnDetailOpen(false)}
							expandThreadID={selectedThread?.threadID}
						/>

						{activeConnection && (
							<TemplateDialog
								isOpen={templateDialogOpen}
								onClose={() => {
									setTemplateDialogOpen(false);
									setActiveConnection(null);
								}}
								connectionData={activeConnection.connectionData}
								connID={activeConnection.connID}
								stageID={activeConnection.currStageID}
								onRecordAdded={null}
							/>
						)}

					</div>
				</DndContext>
			</div>
		</TooltipProvider>
	);
};

export default KanbanThreads;